import { TrainingClass } from "@/hooks/useTrainingClasses";
import { SportBadge } from "./SportBadge";
import { Badge } from "@/components/ui/badge";
import { MapPin, Clock, Users, Star, FileText, BadgeCheck } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sport } from "@/types";

interface MiniClassCardProps {
  trainingClass: TrainingClass & {
    coach?: {
      id: string;
      name: string;
      avatar: string | null;
      coach_profile?: {
        is_verified: boolean;
      } | null;
    };
  };
  onClick?: () => void;
}

const dayNames = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

export const MiniClassCard = ({ trainingClass, onClick }: MiniClassCardProps) => {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('vi-VN').format(price) + 'đ';
  };

  const getTimeDisplay = () => {
    if (trainingClass.class_type === 'single_session' && trainingClass.start_time && trainingClass.end_time) {
      return `${trainingClass.start_time.slice(0, 5)} - ${trainingClass.end_time.slice(0, 5)}`;
    }
    if (trainingClass.schedules && trainingClass.schedules.length > 0) {
      const schedule = trainingClass.schedules[0];
      return `${schedule.start_time.slice(0, 5)} - ${schedule.end_time.slice(0, 5)}`;
    }
    return null;
  };

  const getScheduleDays = () => {
    if (trainingClass.schedules && trainingClass.schedules.length > 0) {
      return trainingClass.schedules.map(s => dayNames[s.day_of_week]).join(', ');
    }
    return null;
  };

  const isCoachVerified = trainingClass.coach?.coach_profile?.is_verified;

  return (
    <div 
      onClick={onClick}
      className="match-tile cursor-pointer hover:border-accent/50 transition-all"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <SportBadge sport={trainingClass.sport as Sport} size="sm" />
          <Badge variant={trainingClass.class_type === 'course' ? 'default' : 'secondary'} className="text-xs">
            {trainingClass.class_type === 'course' ? 'Khóa học' : 'Buổi lẻ'}
          </Badge>
          {trainingClass.status === 'full' && (
            <Badge variant="destructive" className="text-xs">Hết chỗ</Badge>
          )}
          {trainingClass.curriculum_url && (
            <Badge variant="outline" className="text-xs gap-0.5">
              <FileText className="h-3 w-3" />
            </Badge>
          )}
        </div>
        <div className="text-right shrink-0">
          <span className="text-sm font-bold text-accent">{formatPrice(trainingClass.price_per_session)}</span>
          <span className="text-xs text-muted-foreground">/buổi</span>
        </div>
      </div>

      {/* Title */}
      <h4 className="font-semibold text-foreground line-clamp-1 mb-2">{trainingClass.title}</h4>

      {/* Coach */}
      <div className="flex items-center gap-2 mb-2">
        <Avatar className="h-5 w-5">
          <AvatarImage src={trainingClass.coach?.avatar || undefined} />
          <AvatarFallback className="text-xs">{trainingClass.coach?.name?.charAt(0) || 'C'}</AvatarFallback>
        </Avatar>
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
          {trainingClass.coach?.name || 'HLV'}
          {isCoachVerified && (
            <BadgeCheck className="h-3 w-3 text-primary fill-primary/20" />
          )}
        </span>
      </div>

      {/* Info row */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <MapPin className="h-3 w-3 text-accent" />
          <span className="truncate max-w-[100px]">{trainingClass.court?.name || trainingClass.location}</span>
        </div>
        {getTimeDisplay() && (
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{getTimeDisplay()}</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <Users className="h-3 w-3" />
          <span>{trainingClass.current_students}/{trainingClass.max_students}</span>
        </div>
      </div>

      {/* Schedule days for courses */}
      {trainingClass.class_type === 'course' && getScheduleDays() && (
        <div className="mt-2 text-xs text-muted-foreground">
          Lịch học: {getScheduleDays()}
        </div>
      )}
    </div>
  );
};
